import {
  AttachApplicationAttachmentResult,
  AttachmentResult,
  AttachmentType,
  GetApplicationsQueryResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import {
  makeFilenameUnique,
  removeApplicationFile,
  uploadAddedApplicationFile,
} from '../../helpers/attachmentApiHelpers';
import { addToSequence } from '../../helpers/submitSequenceHelpers';
import { BaseTracker, SubmitItem, SubmitStatus } from '../../types/submitSequence';
import { CertReqAttachmentMetadata } from './requirementKeys';
import { baseRequirementType } from './requirementTypes';

/**
 * Standardtypen for certreq-vedlegg.
 *
 * Regelen: **AttachmentType settes bare når en nedstrøms pipeline krever den.**
 * Ellers er kravtypen i metadata (`certreq.requirementType`) sannheten om hva
 * fila dokumenterer, og vedlegget lastes opp som Na.
 */
export const CERTREQ_ATTACHMENT_TYPE = AttachmentType.Na;

/**
 * Unntakene fra regelen over — kravtypene som har en pipeline bak seg.
 *
 * SID-sertifikatets foto, navnetrekk og legitimasjon går inn i saksbehandlerens
 * bilderedigering: den beskjærer originalen til `PhotoCropped`/
 * `SignatureCropped` i samme gruppe og synker resultatet til felles adressat.
 * Den pipelinen finner filene på AttachmentType, ikke på kravtypen, og derfor —
 * og bare derfor — mapper disse tre.
 */
export const REQUIREMENT_ATTACHMENT_TYPES: { [requirementType: string]: AttachmentType } = {
  ProfilePicture: AttachmentType.Photo,
  Signature: AttachmentType.Signature,
  IdCard: AttachmentType.NorwegianPassportOrId,
};

/**
 * Beskjæringen og originalen er det eneste paret som får dele en gruppe.
 *
 * Ligger det allerede en beskjæring i gruppa, er det altså originaltypen nye
 * filer skal ha — ikke beskjæringens egen. Uten dette ville en ny original bli
 * lastet opp som «beskåret», og pipelinen ville tro at jobben var gjort.
 */
const CROPPED_ORIGINALS: { [croppedType: string]: AttachmentType } = {
  [AttachmentType.PhotoCropped]: AttachmentType.Photo,
  [AttachmentType.SignatureCropped]: AttachmentType.Signature,
};

/**
 * Typen gruppa allerede holder, om noen.
 *
 * Backend krever **samme AttachmentType per gruppe** (med paret over som eneste
 * unntak), og avviser en fil som bryter med den. Gruppa er derfor sterkere enn
 * mappingen vår: finnes den fra før — for eksempel med Na-vedlegg lastet opp før
 * mappingen kom — arver nye filer dens type.
 *
 * Vi ser på hele gruppa, ikke bare vedleggene med vår egen kravnøkkel: en
 * beskjæring saksbehandleren har lagt inn hører til gruppa uten å bære nøkkelen.
 */
export const groupAttachmentType = (
  application?: GetApplicationsQueryResult | null,
  group?: string | null
): AttachmentType | undefined => {
  if (!group) return undefined;
  const existing = application?.attachments?.find((a) => a.group === group && !!a.type);
  if (!existing?.type) return undefined;
  return CROPPED_ORIGINALS[existing.type] || existing.type;
};

/** AttachmentType-en et krav skal laste opp med — gruppa først, så mappingen, ellers Na. */
export const certReqAttachmentType = (
  requirementType?: string | null,
  existingGroupType?: AttachmentType | null
): AttachmentType => {
  if (existingGroupType) return existingGroupType;
  const base = baseRequirementType(requirementType);
  return (base && REQUIREMENT_ATTACHMENT_TYPES[base]) || CERTREQ_ATTACHMENT_TYPE;
};

/** Filtypene et krav normalt tar imot: bilder eller skannet PDF. */
export const CERTREQ_ALLOWED_FILE_TYPES = '.png,.jpg,.jpeg,.pdf';

/** Foto og navnetrekk er bilder — en PDF kommer ikke gjennom bilderedigeringen. */
const IMAGE_ONLY_FILE_TYPES = '.png,.jpg,.jpeg';

/**
 * Filtypene filvelgeren tilbyr for et krav.
 *
 * Foto og navnetrekk begrenses til bilder, slik de gamle stegene `AddPhoto` og
 * `AddSignature` alltid har gjort. Legitimasjon står igjen med PDF-en — et pass
 * skannes ofte til PDF, og `AddNorwegianId` tar imot den.
 */
export const certReqAllowedFileTypes = (requirementType?: string | null): string => {
  const base = baseRequirementType(requirementType);
  const type = base ? REQUIREMENT_ATTACHMENT_TYPES[base] : undefined;
  return type === AttachmentType.Photo || type === AttachmentType.Signature
    ? IMAGE_ONLY_FILE_TYPES
    : CERTREQ_ALLOWED_FILE_TYPES;
};

export type CertReqAttachmentTracker = BaseTracker & {
  addedAttachments?: { [name: string]: SubmitStatus<AttachApplicationAttachmentResult> };
  deletedAttachments?: SubmitStatus[];
};

export type CertReqAttachmentParams = {
  application: GetApplicationsQueryResult;
  /** `certreq:{certificateCode}#{requirementId}` — settes på simulatorResultItemUri. */
  requirementKey: string;
  /** Kravtypen, som avgjør AttachmentType-en. Se `certReqAttachmentType`. */
  requirementType?: string;
  metadata: CertReqAttachmentMetadata;
  /** Gruppa kravet allerede har vedlegg i. Er den tom lager backend en ny Guid. */
  existingGroup?: string | null;
  files: File[];
  removedAttachments: AttachmentResult[];
};

/**
 * Bygger opplastings-/slettesekvensen for ett krav.
 *
 * Vi gjenbruker uploadAddedApplicationFile/removeApplicationFile fra
 * attachmentApiHelpers, men trenger en egen sekvens fordi de eksisterende
 * byggerne der ikke sender `metadata` videre.
 */
export const buildCertReqAttachmentSequence = ({
  application,
  requirementKey,
  requirementType,
  metadata,
  existingGroup,
  files,
  removedAttachments,
}: CertReqAttachmentParams): SubmitItem<CertReqAttachmentTracker>[] => {
  const sequence: SubmitItem<CertReqAttachmentTracker>[] = [];

  // Én type for hele sekvensen: filene havner i samme gruppe, og gruppa tåler
  // bare én. Finnes gruppa fra før, er det dens type som gjelder.
  const attachmentType = certReqAttachmentType(
    requirementType,
    groupAttachmentType(application, existingGroup)
  );

  const usedFilenames = new Set<string>();
  if (application.attachments) {
    application.attachments.forEach((a) => usedFilenames.add(a.blobName || ''));
  }

  removedAttachments.forEach((attachment, removeIndex) => {
    addToSequence(sequence, {
      labelId: 'submitsteps.fileremoval',
      formatParams: { filename: attachment.blobName },
      apiCall: () => removeApplicationFile(application.uid || '', attachment),
      statusSelector: (ctx) => ctx.deletedAttachments?.[removeIndex],
      statusSetter: (ctx, status) => ({
        ...ctx,
        deletedAttachments: [
          ...(ctx.deletedAttachments || []).slice(0, removeIndex),
          status,
          ...(ctx.deletedAttachments || []).slice(removeIndex + 1),
        ],
      }),
    });
  });

  files.forEach((file) => {
    const uniqueFile = makeFilenameUnique(file, usedFilenames);
    usedFilenames.add(uniqueFile.name);
    addToSequence(sequence, {
      labelId: 'submitsteps.fileupload',
      formatParams: { filename: uniqueFile.name },
      apiCall: (ctx) => {
        // Første opplasting oppretter gruppa; resten arver Guid-en backend ga oss.
        const createdGroup = Object.values(ctx.addedAttachments || {})[0]?.result?.group;
        return uploadAddedApplicationFile(application.uid || '', uniqueFile, attachmentType, {
          group: existingGroup || createdGroup,
          metadata,
          simulatorResultItemUri: requirementKey,
        });
      },
      statusSelector: (ctx) => ctx.addedAttachments?.[uniqueFile.name],
      statusSetter: (ctx, status) => ({
        ...ctx,
        addedAttachments: {
          ...ctx.addedAttachments,
          [uniqueFile.name]: status,
        },
      }),
    });
  });

  return sequence;
};

/** Vedleggene som hører til ett krav, funnet på kravnøkkelen. */
export const attachmentsForRequirement = (
  application?: GetApplicationsQueryResult | null,
  requirementKey?: string | null
): AttachmentResult[] => {
  if (!requirementKey) return [];
  return application?.attachments?.filter((a) => a.simulatorResultItemUri === requirementKey) || [];
};
